import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";

import { useCart, useDealer } from "@/contexts";
import { CartHelper } from "@/helpers";
import { useDataApi } from "@/hooks";
import { colors } from "@/theme";

import { ReactComponent as SaveOrderIcon } from "../../../assets/save-order-icon.svg";
import { ReactComponent as LogoImage } from "../../../assets/navbar-DTV-logo.svg";
import { Button, Divider, Box, useMediaQuery, useTheme } from "@mui/material";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";

import { StyledOffSiteProductIcon } from './styles';
import * as S from "./styles";

import moment from "moment-timezone";
import axios from "axios";

const HomePageHeader: React.FC = () => {
  const [isSaveOrderDialogOpen, setIsSaveOrderDialogOpen] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isApiProcessing, setIsApiProcessing] = useState(false);
  const [isPostCompleted, setIsPostCompleted] = useState(false);
  const [isGetCompleted, setIsGetCompleted] = useState(false);

  const { _get, post, response, error, _delete, put } = useDataApi();
  const { cart, setOrderId, clear } = useCart();
  const { setNewSession } = useDealer();
  const navigate = useNavigate();
  const location = useLocation();

  const isOffsitePage = location.pathname === "/offsite";
  const isCheckoutPage = location.pathname === "/checkout";

  useEffect(() => {
    if (!isPostCompleted) {
      return;
    }

    if (error) {
      console.error("Error saving order:", error);
      return;
    }

    if (response?.status === 201) {
      console.log("Order saved successfully. Response:", response.data);
      if (response.data?.orderId && setOrderId) {
        setOrderId(response.data.orderId);
      }
      navigate("/session-saved");
    }

    setIsPostCompleted(false);
  }, [isPostCompleted, error, response, setOrderId, navigate]);

  useEffect(() => {
    const handleGetResponse = async () => {
      if (!isGetCompleted) {
        return;
      }

      if (error) {
        console.error("Error checking existing order:", error);
        return;
      }

      if (response?.status === 200) {
        console.log("GET response:", response.data);
        if (response.data && response.data.status === 1) {
          console.log("Saved order already exists.", cart);

          // Calculate estimated delivery date (7 days from now)
          const estimatedDeliveryDate = moment()
            .tz("America/Los_Angeles")
            .add(7, "days")
            .format();

          const postData = {
            card: cart?.card,
            cardExpired: cart?.cardExpired,
            orderId: cart?.orderId,
            orderIdentifier: cart?.orderIdentifier,
            dealerId: cart?.dealerId,
            masterDealerId: cart?.masterDealerId,
            total: (
              CartHelper.getCartSubtotal(cart) -
              (cart?.discount || 0) -
              (cart?.coopEligible || 0)
            ).toFixed(2),
            claimFee: cart?.claimFee || 0,
            shippingFee: (() => {
              const baseShippingFee = CartHelper.getShippingFee(cart) || 0;
              console.log('Base shipping fee:', baseShippingFee);

              // Calculate total shipping quantity
              const totalShippingQuantity = cart?.products?.reduce((total: number, p) => {
                let quantityToAdd = 0;

                if (p.offsite) {
                  quantityToAdd = p.quantity || 0;
                  console.log('Offsite product:', p.productDescription, 'quantity:', quantityToAdd);
                } else if (p.selectedToShip === true || (p.shippingQuantity && p.shippingQuantity > 0)) {
                  quantityToAdd = p.shippingQuantity || 0;
                  console.log('Selected to ship product:', p.productDescription, 'shippingQuantity:', quantityToAdd);
                }

                console.log('Adding to total:', quantityToAdd);
                return total + quantityToAdd;
              }, 0) || 0;

              console.log('Total shipping quantity:', totalShippingQuantity);
              const finalFee = totalShippingQuantity * baseShippingFee;
              console.log('Final shipping fee:', finalFee);
              return finalFee;
            })(),
            discount: cart?.discount || 0,
            discountComment: cart?.discountComment || "",
            // estimatedDeliveryDate: estimatedDeliveryDate,
            status: 1,
            offsite: isOffsitePage,
            products: cart?.products?.map((p) => ({
              ...p,
              selectedToShip: p.offsite ? true : Boolean(p.selectedToShip || (p.shippingQuantity ?? 0) > 0),
              shippingQuantity: p.offsite ? (p.quantity || 0) : (p.selectedToShip ? (p.shippingQuantity || p.quantity || 0) : 0),
            })),
            coopUsed: CartHelper.getSubtotalCoopTotal(cart),
            creditCardUsed: 0,
            ship: cart?.products?.some(p => p.selectedToShip || (p.shippingQuantity ?? 0) > 0),
            trackingNumber: "",
            couponAmount: cart?.couponAmount || 0,
            couponQuantity: cart?.couponQuantity || 0,
            couponUsed: cart?.couponUsed || 0,
            dealerFirstName: cart?.dealerFirstName || "",
            dealerLastName: cart?.dealerLastName || "",
            // dateTimeCreated: moment().tz("America/Los_Angeles").format(),
            errorOccured: cart?.errorOccured,
            errorDescription: cart?.errorDescription
          };
          console.log("Attempting to post order with data:", postData);

          try {
            await put(`${process.env.REACT_APP_REST_BASE_URL}/Orders/save`, postData);
            setIsPostCompleted(true);
            navigate("/session-saved");
          } catch (error: any) {
            console.error("Error paying order:", error);
            if (error.response) {
              console.error("Error response data:", error.response.data);
              console.error("Error response status:", error.response.status);
            }
          }
          return;
        }
      }

      setIsGetCompleted(false);
    };

    handleGetResponse();
  }, [isGetCompleted, error, response, cart, post, navigate]);

  const handleSaveOrder = async () => {
    console.log("Save Order clicked");
    console.log("Current cart:", cart);
    if (!cart) return
    setIsApiProcessing(true);

    try {
      const isOffsite = (window.location.pathname).includes("offsite") || location.state?.mode === "offsite";

      console.log(isOffsite)

      let prevOrderOffsite = null;

      // First check if order already exists
      if (cart?.orderId) {
        console.log("Fetching existing order...");
        const existingOrder = await axios.get(
          `${process.env.REACT_APP_REST_BASE_URL}/Orders/getorderfull/${cart.orderId}`
        )
        prevOrderOffsite = existingOrder?.data.offsite
        // setIsGetCompleted(true);
        // return; // Wait for the useEffect to handle the response
      }
      // Calculate estimated delivery date (7 days from now)
      const estimatedDeliveryDate = moment()
        .tz("America/Los_Angeles")
        .add(7, "days")
        .format();
      const filteredProducts = cart?.products.filter(item => item.offsite == isOffsite)

      const postData = {
        card: cart?.card,
        orderId: prevOrderOffsite == isOffsite ? cart?.orderId : undefined,
        cardExpired: cart?.cardExpired,
        orderIdentifier: cart?.orderIdentifier,
        dealerId: cart?.dealerId,
        masterDealerId: cart?.masterDealerId,
        total: (
          CartHelper.getCartSubtotal(cart) 
          // -
          // (isOffsite ? (cart?.offSiteDiscount || 0) : (cart?.discount || 0)) 
          // (cart?.coopEligible || 0)
        ).toFixed(2),
        claimFee: cart?.claimFee || 0,
        shippingFee: (() => {
          const baseShippingFee = CartHelper.getShippingFee(cart) || 0;
          console.log('Base shipping fee:', baseShippingFee);
          // Calculate total shipping quantity
          const totalShippingQuantity = filteredProducts?.reduce((total: number, p) => {
            let quantityToAdd = 0;

            console.log('Processing product:', {
              description: p.productDescription,
              offsite: p.offsite,
              selectedToShip: p.selectedToShip,
              shippingQuantity: p.shippingQuantity,
              quantity: p.quantity
            });

            if (p.offsite) {
              quantityToAdd = p.shippingQuantity || 0;
              console.log('Offsite product:', p.productDescription, 'quantity:', quantityToAdd);
            } else if (p.selectedToShip === true || (p.shippingQuantity && p.shippingQuantity > 0)) {
              quantityToAdd = p.shippingQuantity || 0;
              console.log('Selected to ship product:', p.productDescription, 'shippingQuantity:', quantityToAdd);
            }

            console.log('Adding to total:', quantityToAdd);
            return total + quantityToAdd;
          }, 0) || 0;

          console.log('Total shipping quantity:', totalShippingQuantity);
          const finalFee = totalShippingQuantity * baseShippingFee;
          console.log('Final shipping fee:', finalFee);
          return finalFee;
        })(),
        discount: cart?.discount || 0,
        discountComment: cart?.discountComment || "",
        // estimatedDeliveryDate: estimatedDeliveryDate,
        status: 1,
        offsite: isOffsite,
        products: filteredProducts?.map((p) => ({
          ...p,
          selectedToShip: !!p.shippingQuantity,
        })),
        coopUsed: CartHelper.getSubtotalCoopTotal(cart),
        creditCardUsed: 0,
        ship: filteredProducts?.some(p => p.selectedToShip || (p.shippingQuantity ?? 0) > 0)||isOffsite,
        trackingNumber: "",
        couponAmount: cart?.couponAmount || 0,
        couponQuantity: cart?.couponQuantity || 0,
        couponUsed: cart?.couponUsed || 0,
        dealerFirstName: cart?.dealerFirstName || "",
        dealerLastName: cart?.dealerLastName || "",
        // dateTimeCreated: moment().tz("America/Los_Angeles").format(),
        errorOccured: cart?.errorOccured,
        errorDescription: cart?.errorDescription
      };

      console.log("Attempting to post order with data:", postData);

      await put(`${process.env.REACT_APP_REST_BASE_URL}/Orders/save`, postData);
      setIsPostCompleted(true);
      navigate("/session-saved");
    } catch (error: any) {
      console.error("Error saving order:", error);
      if (error.response) {
        console.error("Error response data:", error.response.data);
        console.error("Error response status:", error.response.status);
      }
    } finally {
      setIsApiProcessing(false);
    }
  };
  
  const handleCancelOrder = () => {
    console.log('Cancelling order:',);
    setIsCancelDialogOpen(true);
  };

  const handleConfirmCancelOrder = async () => {
    if (cart?.orderId) {
      try {
        console.log('Canceling saved order:', cart.orderId);
        await _delete(`${process.env.REACT_APP_REST_BASE_URL}/Orders/cancel/${cart.orderId}`);
        console.log('Order cancellation successful');
      } catch (error) {
        console.error('Error canceling saved order:', error);
        if (axios.isAxiosError(error)) {
          console.error('Error details:', {
            status: error.response?.status,
            data: error.response?.data,
            message: error.message
          });
        }
      }
    }
    clear?.();
    setNewSession();
    setIsCancelDialogOpen(false);
    navigate("/login");
  };

  const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (checked) {
      navigate("/offsite");
    } else {
      navigate("/");
    }
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  if (isCheckoutPage) {
    return (
      <S.HomePageHeaderContainer>
        <S.HomePageHeaderLeftContainer>
          <Button
            variant="text"
            color="inherit"
            onClick={handleBackClick}
            style={{
              color: "#fff",
              fontWeight: 500,
              textTransform: "none",
              fontSize: "18px",
              fontFamily: "PF DIN Text Pro",
              display: "flex",
              alignItems: "center",
              padding: "2px 8px",
              minWidth: "fit-content",
              whiteSpace: "nowrap",
            }}
            startIcon={
              <ArrowBackIcon
                style={{ height: 24, width: 24 }}
              />
            }
          >
            Back to Cart
          </Button>
        </S.HomePageHeaderLeftContainer>
        <S.HomePageHeaderCenterContainer>
          <LogoImage style={{ height: 22, width: 113, display: "block" }} />
        </S.HomePageHeaderCenterContainer>
        <S.HomePageHeaderRightContainer>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "nowrap",
            }}
          >


            <Button
              variant="text"
              color="inherit"
              style={{
                color: "#fff",
                fontWeight: 500,
                textTransform: "none",
                fontSize: "18px",
                fontFamily: "PF DIN Text Pro",
                display: "flex",
                alignItems: "center",
                padding: "2px 8px",
                minWidth: "fit-content",
                whiteSpace: "nowrap",
              }}
              startIcon={
                <SaveOrderIcon
                  style={{ height: 28, width: 28, marginRight: 2 }}
                />
              }
              onClick={handleSaveOrder}
              disabled={isApiProcessing}
            >
              Save Order
            </Button>

            <Button
              variant="text"
              color="inherit"
              onClick={handleCancelOrder}
              style={{
                color: "#fff",
                fontWeight: 500,
                textTransform: "none",
                fontSize: "18px",
                fontFamily: "PF DIN Text Pro",
                display: "flex",
                alignItems: "center",
                padding: "2px 8px",
                minWidth: "fit-content",
                whiteSpace: "nowrap",
              }}
              startIcon={
                <ClearOutlinedIcon
                  fontSize="small"
                  style={{ height: 24, width: 24 }}
                />
              }
            >
              Cancel
            </Button>
          </Box>
          <Dialog
            open={isCancelDialogOpen}
            onClose={() => setIsCancelDialogOpen(false)}
            maxWidth="xs"
            PaperProps={{
              sx: {
                borderRadius: 3,
                px: 4,
                fontFamily: "PF DIN Text Pro",
                boxShadow: '0px 2px 16px rgba(0,0,0,0.10)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              },
            }}
          >
            <DialogContent sx={{ textAlign: "center", pt: 6, pb: 2 }}>
              <Typography
                sx={{
                  fontSize: 22,
                  fontWeight: 400,
                  fontFamily: "PF DIN Text Pro",
                  color: colors.neutralGrey800,
                  mb: 2,
                }}
                style={{ fontSize: '22px' }}
              >
                Are you sure you want to cancel<br />this order?
              </Typography>
            </DialogContent>
            <DialogActions sx={{ justifyContent: "center", pb: 4, gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setIsCancelDialogOpen(false)}
                sx={{
                  color: colors.neutralGrey800,
                  borderColor: colors.neutralGrey800,
                  minWidth: 160,
                  borderRadius: 2,
                  fontWeight: 400,
                  fontSize: 20,
                  textTransform: "none",
                  height: 56,
                  background: '#fff',
                  fontFamily: "PF DIN Text Pro",
                  boxShadow: 'none',
                  '&:hover': {
                    background: colors.neutralGrey300,
                    borderColor: colors.neutralGrey800,
                  },
                }}
              >
                Go Back
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirmCancelOrder}
                sx={{
                  minWidth: 160,
                  borderRadius: 2,
                  fontWeight: 400,
                  fontSize: 20,
                  textTransform: "none",
                  background: '#E5391A',
                  color: '#fff',
                  height: 56,
                  fontFamily: "PF DIN Text Pro",
                  boxShadow: 'none',
                  '&:hover': {
                    background: '#C62813',
                  },
                }}
              >
                Cancel Order
              </Button>
            </DialogActions>
          </Dialog>
          {/* <DealerPanel /> */}
        </S.HomePageHeaderRightContainer>
      </S.HomePageHeaderContainer>
    );
  }

  return (
    <S.HomePageHeaderContainer>
      <S.HomePageHeaderLeftContainer>
        <S.LogoImageContainer>
          <LogoImage style={{ height: 22, width: 113, display: "block" }} />
        </S.LogoImageContainer>
      </S.HomePageHeaderLeftContainer>
      <S.HomePageHeaderCenterContainer>
        {/* Center section intentionally left empty for now */}
      </S.HomePageHeaderCenterContainer>
      <S.HomePageHeaderRightContainer>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "nowrap",
          }}
        >
          <S.ToggleLabelOutside
            active={!isOffsitePage}
            onClick={() => navigate("/")}
          >
            In-Store
          </S.ToggleLabelOutside>
          <S.TogglePill>
            <S.ToggleIconWrapper
              active={!isOffsitePage}
              clickable
              onClick={() => navigate("/")}
            >
              <StorefrontOutlinedIcon sx={{ fontSize: 22 }} />
            </S.ToggleIconWrapper>
            <S.ToggleIconWrapper
              active={isOffsitePage}
              clickable
              onClick={() => navigate("/offsite")}
            >
              <StyledOffSiteProductIcon active={isOffsitePage} />
            </S.ToggleIconWrapper>
          </S.TogglePill>
          <S.ToggleLabelOutside
            active={isOffsitePage}
            onClick={() => navigate("/offsite")}
          >
            Off-Site
          </S.ToggleLabelOutside>
          <Divider
            orientation="vertical"
            flexItem
            style={{
              margin: "0 8px",
              background: colors.white,
              height: 30,
              alignSelf: "center",
              width: 1,
            }}
          />
          <Button
            variant="text"
            color="inherit"
            style={{
              color: "#fff",
              fontWeight: 500,
              textTransform: "none",
              fontSize: "18px",
              fontFamily: "PF DIN Text Pro",
              display: "flex",
              alignItems: "center",
              padding: "2px 8px",
              minWidth: "fit-content",
              whiteSpace: "nowrap",
            }}
            startIcon={
              <SaveOrderIcon
                style={{ height: 28, width: 28, marginRight: 2 }}
              />
            }
            onClick={handleSaveOrder}
            disabled={isApiProcessing}
          >
            Save Order
          </Button>

          <Button
            variant="text"
            color="inherit"
            onClick={handleCancelOrder}
            style={{
              color: "#fff",
              fontWeight: 500,
              textTransform: "none",
              fontSize: "18px",
              fontFamily: "PF DIN Text Pro",
              display: "flex",
              alignItems: "center",
              padding: "2px 8px",
              minWidth: "fit-content",
              whiteSpace: "nowrap",
            }}
            startIcon={
              <ClearOutlinedIcon
                fontSize="small"
                style={{ height: 24, width: 24 }}
              />
            }
          >
            Cancel
          </Button>
        </Box>
        {/* <DealerPanel /> */}
      </S.HomePageHeaderRightContainer>
      {/* Save Order Dialog */}
      <Dialog
        open={isSaveOrderDialogOpen}
        onClose={() => setIsSaveOrderDialogOpen(false)}
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 0,
            minWidth: 350,
            fontFamily: "PF DIN Text Pro",
          },
        }}
      >
        <DialogContent sx={{ textAlign: "center", pt: 6, pb: 2 }}>
          <Typography
            sx={{
              fontSize: "24px",
              fontWeight: 400,
              fontFamily: "PF DIN Text Pro",
            }}
          >
            This customer has a saved order,
          </Typography>
          <Typography
            sx={{
              fontSize: "24px",
              fontWeight: 400,
              mb: 2,
              fontFamily: "PF DIN Text Pro",
            }}
          >
            we cannot save another order.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 4 }}>
          <Button
            variant="outlined"
            onClick={() => setIsSaveOrderDialogOpen(false)}
            sx={{
              color: colors.neutralGrey800,
              borderColor: colors.neutralGrey800,
              minWidth: 120,
              borderRadius: 2,
              fontWeight: 500,
              fontSize: 20,
              textTransform: "none",
              mr: 2,
              fontFamily: "PF DIN Text Pro",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => setIsSaveOrderDialogOpen(false)}
            sx={{
              minWidth: 120,
              borderRadius: 2,
              fontWeight: 500,
              fontSize: 20,
              textTransform: "none",
              background: "#0070f3",
              color: "#fff",
              ":hover": { background: "#0059c1" },
              fontFamily: "PF DIN Text Pro",
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
      {/* Cancel Order Confirmation Dialog */}
      <Dialog
        open={isCancelDialogOpen}
        onClose={() => setIsCancelDialogOpen(false)}
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 3,
            px: 4,
            fontFamily: "PF DIN Text Pro",
            boxShadow: '0px 2px 16px rgba(0,0,0,0.10)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          },
        }}
      >
        <DialogContent sx={{ textAlign: "center", pt: 6, pb: 2 }}>
          <Typography
            sx={{
              fontSize: 22,
              fontWeight: 400,
              fontFamily: "PF DIN Text Pro",
              color: colors.neutralGrey800,
              mb: 2,
            }}
            style={{ fontSize: '22px' }}
          >
            Are you sure you want to cancel<br />this order?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 4, gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setIsCancelDialogOpen(false)}
            sx={{
              color: colors.neutralGrey800,
              borderColor: colors.neutralGrey800,
              minWidth: 160,
              borderRadius: 2,
              fontWeight: 400,
              fontSize: 20,
              textTransform: "none",
              height: 56,
              background: '#fff',
              fontFamily: "PF DIN Text Pro",
              boxShadow: 'none',
              '&:hover': {
                background: colors.neutralGrey300,
                borderColor: colors.neutralGrey800,
              },
            }}
          >
            Go Back
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmCancelOrder}
            sx={{
              minWidth: 160,
              borderRadius: 2,
              fontWeight: 400,
              fontSize: 20,
              textTransform: "none",
              background: '#E5391A',
              color: '#fff',
              height: 56,
              fontFamily: "PF DIN Text Pro",
              boxShadow: 'none',
              '&:hover': {
                background: '#C62813',
              },
            }}
          >
            Cancel Order
          </Button>
        </DialogActions>
      </Dialog>
    </S.HomePageHeaderContainer>
  );
};

export default HomePageHeader;
